import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { Address } from '@ridgewill/brand/react';
import { Field, Note, Problem, button, input } from './form.js';
import { Modal } from './Modal.js';
import { Turnstile } from './Turnstile.js';
import type { CaptchaConfig } from '../lib/api.js';
import { addressesAgree } from '../lib/email-change.js';
import {
  LINK_LIFETIME_TEXT,
  VERIFY_FIRST,
  correctEmail,
  loadCaptcha,
  resendLink,
} from '../lib/email-verification.js';

/**
 * "Your email address isn't verified yet", and the way out of it
 * (RidgeWill/account#112, over RidgeWill/auth#587).
 *
 * ── A BANNER ON EVERY SCREEN, AND A CONTROL ON THE EMAIL CARD ────────
 *
 * The banner goes in the Shell's `banner` slot, beside the recovery review, for
 * that prompt's reason: an account-level condition belongs to the page rather
 * than to whichever screen happens to be open. Keyodo's app links to
 * `/security#email`, so the same control sits in the Email address card too;
 * somebody who arrives there from a "verify your email" prompt should find the
 * answer on the card they were sent to.
 *
 * ── NO DISMISS, LIKE THE RECOVERY REVIEW ─────────────────────────────
 *
 * It goes away when the address is verified, and that is the only way. A
 * dismissed banner would leave somebody wondering why Add a passkey is missing.
 */
export function EmailNotVerifiedBanner({ onChanged }: { onChanged: () => void }) {
  return (
    <aside
      /**
       * `status`, not `alert`: a standing condition somebody arrives at on every
       * page load, not something that just happened in front of them.
       */
      role="status"
      aria-label="Email not verified"
      style={{
        border: `1px solid ${C.border}`,
        borderLeft: `4px solid ${C.warnDot}`,
        borderRadius: '6px',
        background: C.surface,
        padding: '1rem 1.25rem',
        marginBottom: '1.5rem',
      }}
    >
      <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Email not verified</p>
      <p style={{ margin: '0 0 0.75rem', lineHeight: 1.5 }}>
        Your email address isn&rsquo;t verified yet. Some features stay locked until you verify it.
        Open the link we emailed you, or ask for a new one.
      </p>
      <ResendLink onChanged={onChanged} />
    </aside>
  );
}

/**
 * The same control, for the Email address card on Security.
 *
 * Says which features are locked, because this card is where somebody looks
 * when one of them is missing.
 */
export function UnverifiedEmailNotice({ onChanged }: { onChanged: () => void }) {
  return (
    <div role="status" style={card}>
      <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem', color: C.warnText }}>
        This address isn&rsquo;t verified yet
      </h3>
      <p style={{ margin: '0 0 .6rem', lineHeight: 1.5, fontSize: '.95rem' }}>
        Until it is, you can&rsquo;t add a passkey, turn on two-factor, set a recovery address or
        change your email address. Open the link we emailed you, or ask for a new one. Connecting
        Google, Microsoft or Apple with this same address also verifies it. If the address above
        has a typo, you can correct it once.
      </p>
      <ResendLink onChanged={onChanged} offerCorrection />
    </div>
  );
}

/** The one line a locked control shows in its place. */
export function VerifyFirst() {
  return <Note>{VERIFY_FIRST}</Note>;
}

/**
 * "Resend link", and the dialog behind it.
 *
 * A dialog rather than an inline form because it holds a captcha and, on a
 * mismatch, a second form. Inline, the banner would grow by a widget and two
 * address fields on every screen of the portal.
 */
export function ResendLink({
  onChanged,
  offerCorrection = false,
}: {
  onChanged: () => void;
  /**
   * A second button that opens straight on the correction form. Only on the
   * Security card, which shows the address: somebody looking at a typo there
   * should not have to fail a resend first to be offered the fix. The banner
   * stays with one action.
   */
  offerCorrection?: boolean;
}) {
  const [open, setOpen] = useState(false);
  /**
   * Which step the dialog shows. NOT reset on close, only on the next open, for
   * the reason TwoFactor gives: `Modal` keeps rendering through its exit, and a
   * step reset on close would flash the first form while it fades.
   */
  const [step, setStep] = useState<Step>({ name: 'resend' });

  return (
    <>
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          style={{ ...button, marginTop: 0 }}
          onClick={() => {
            setStep({ name: 'resend' });
            setOpen(true);
          }}
        >
          Resend link
        </button>
        {offerCorrection ? (
          <button
            type="button"
            style={{ ...button, marginTop: 0 }}
            onClick={() => {
              setStep({ name: 'correct' });
              setOpen(true);
            }}
          >
            Correct a typo
          </button>
        ) : null}
      </div>

      <Modal
        open={open}
        title={step.name === 'correct' || step.name === 'corrected' ? 'Change your email address' : 'Resend the verification link'}
        onClose={() => setOpen(false)}
      >
        <Steps step={step} setStep={setStep} onClose={() => setOpen(false)} onChanged={onChanged} />
      </Modal>
    </>
  );
}

type Step =
  | { name: 'resend' }
  | { name: 'sent'; address: string }
  | { name: 'correct' }
  | { name: 'corrected'; address: string };

function Steps({
  step,
  setStep,
  onClose,
  onChanged,
}: {
  step: Step;
  setStep: (s: Step) => void;
  onClose: () => void;
  onChanged: () => void;
}) {
  const captcha = useCaptchaConfig();

  if (step.name === 'sent' || step.name === 'corrected') {
    return (
      <>
        <Note tone="ok">
          {step.name === 'corrected' ? (
            <>
              Your email address is now <strong><Address>{step.address}</Address></strong>. We sent a
              new link there. It works for {LINK_LIFETIME_TEXT}.
            </>
          ) : (
            <>
              We sent a new link to <strong><Address>{step.address}</Address></strong>. It works for{' '}
              {LINK_LIFETIME_TEXT}.
            </>
          )}
        </Note>
        <button type="button" style={button} onClick={onClose}>
          Close
        </button>
      </>
    );
  }

  if (step.name === 'correct') {
    return (
      <CorrectForm
        captcha={captcha}
        onCancel={onClose}
        onDone={(address) => {
          // The session is the source of the address on every screen; see App.
          onChanged();
          setStep({ name: 'corrected', address });
        }}
      />
    );
  }

  return (
    <ResendForm
      captcha={captcha}
      onCancel={onClose}
      onSent={(address) => setStep({ name: 'sent', address })}
      onCorrect={() => setStep({ name: 'correct' })}
      onAlreadyVerified={onChanged}
    />
  );
}

/**
 * The account deployment's captcha: `undefined` while asking, `null` when it
 * runs none. Asked when the dialog opens rather than on every page load, so the
 * banner costs nothing for somebody who never presses it.
 */
function useCaptchaConfig(): CaptchaConfig | null | undefined {
  const [config, setConfig] = useState<CaptchaConfig | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void loadCaptcha().then((c) => {
      if (live) setConfig(c);
    });
    return () => {
      live = false;
    };
  }, []);
  return config;
}

/**
 * The token half of a captcha'd form, shared by both forms below.
 *
 * `spent()` after every submit: a Turnstile token is single use, so the one
 * just sent is gone whatever the answer was, and the widget must mint another.
 */
function useCaptchaToken() {
  const [token, setToken] = useState<string | null>(null);
  const [resetSignal, setResetSignal] = useState(0);
  return {
    token,
    setToken,
    resetSignal,
    spent: () => {
      setToken(null);
      setResetSignal((n) => n + 1);
    },
  };
}

function CaptchaSlot({
  captcha,
  action,
  onToken,
  resetSignal,
}: {
  captcha: CaptchaConfig | null | undefined;
  action: string;
  onToken: (t: string | null) => void;
  resetSignal: number;
}) {
  if (captcha === undefined) return <p style={{ color: C.muted, margin: '1rem 0' }}>Loading the security check&hellip;</p>;
  if (captcha === null) return null;
  return <Turnstile siteKey={captcha.siteKey} action={action} onToken={onToken} resetSignal={resetSignal} />;
}

/** Ready to submit, as far as the captcha goes. */
const captchaReady = (captcha: CaptchaConfig | null | undefined, token: string | null) =>
  captcha === null || (captcha !== undefined && token !== null);

function ResendForm({
  captcha,
  onCancel,
  onSent,
  onCorrect,
  onAlreadyVerified,
}: {
  captcha: CaptchaConfig | null | undefined;
  onCancel: () => void;
  onSent: (address: string) => void;
  onCorrect: () => void;
  onAlreadyVerified: () => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offerCorrection, setOfferCorrection] = useState(false);
  const { token, setToken, resetSignal, spent } = useCaptchaToken();

  const ready = email.trim().length > 0 && captchaReady(captcha, token);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setOfferCorrection(false);
    const out = await resendLink(email, token);
    setBusy(false);
    spent();
    if (out.ok) {
      onSent(email.trim());
      return;
    }
    setError(out.message);
    if (out.code === 'EMAIL_MISMATCH' && out.correctionAvailable) setOfferCorrection(true);
    // The banner is now wrong; re-reading the session takes it away.
    if (out.code === 'EMAIL_ALREADY_VERIFIED') onAlreadyVerified();
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <p style={{ margin: '0 0 1rem', lineHeight: 1.5 }}>
        Type the email address you signed up with, and we&rsquo;ll send a new link to it.
      </p>

      {/*
        TYPED, NEVER PREFILLED. The server compares it with the account's
        address, and that comparison is the point: see `resendVerificationLink`.
      */}
      <Field id="resend-email" label="Your email address">
        <input
          id="resend-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          style={input}
          autoFocus
        />
      </Field>

      <CaptchaSlot captcha={captcha} action="resend-verification" onToken={setToken} resetSignal={resetSignal} />

      {error ? <Problem>{error}</Problem> : null}

      {offerCorrection ? (
        <p style={{ margin: '0 0 1rem' }}>
          <button type="button" style={{ ...button, marginTop: 0 }} onClick={onCorrect}>
            Change email address?
          </button>
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
        <button type="submit" disabled={busy || !ready} style={button}>
          {busy ? 'Sending…' : 'Send a new link'}
        </button>
        <button type="button" onClick={onCancel} style={button}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CorrectForm({
  captcha,
  onCancel,
  onDone,
}: {
  captcha: CaptchaConfig | null | undefined;
  onCancel: () => void;
  onDone: (address: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token, setToken, resetSignal, spent } = useCaptchaToken();

  /** Shown only once the second field has something in it, as on the change form. */
  const agree = addressesAgree(email, confirm);
  const mismatch = confirm.trim().length > 0 && !agree;
  const ready = email.trim().length > 0 && agree && captchaReady(captcha, token);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const out = await correctEmail(email, confirm, token);
    setBusy(false);
    spent();
    if (out.ok) {
      onDone(out.email);
      return;
    }
    setError(out.message);
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <p style={{ margin: '0 0 .6rem', lineHeight: 1.5 }}>
        If the address was mistyped when you signed up, you can correct it here. This works once,
        and only before the address is verified.
      </p>
      <Note>
        The change happens straight away. We send a new link to the new address, and the link sent
        to the old one stops working.
      </Note>

      <div style={{ marginTop: '1rem' }}>
        <Field id="correct-email" label="New email address">
          <input
            id="correct-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            style={input}
            autoFocus
          />
        </Field>

        {/* Typed again because a typo here is the very thing being fixed, and
            there is only one correction. `off` so the browser does not echo the
            field above into it. */}
        <Field id="correct-email-confirm" label="Confirm the new email address">
          <input
            id="correct-email-confirm"
            type="email"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="off"
            style={input}
          />
          {mismatch ? <Note tone="bad">These addresses do not match.</Note> : null}
        </Field>
      </div>

      <CaptchaSlot captcha={captcha} action="correct-email" onToken={setToken} resetSignal={resetSignal} />

      {error ? <Problem>{error}</Problem> : null}

      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
        <button type="submit" disabled={busy || !ready} style={button}>
          {busy ? 'Changing…' : 'Change my address'}
        </button>
        <button type="button" onClick={onCancel} style={button}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** The warn trio, as the pending email change card uses it (account#87). */
const card: React.CSSProperties = {
  marginTop: '1rem',
  padding: '1rem',
  border: `1px solid ${C.warnDot}`,
  borderRadius: 8,
  background: C.warnBg,
};
